<script setup lang="ts">
import { URL_IMAGE } from '@/config/constants'
import { languageIdBus } from '@/event/languageIdBus'
import type { Product } from '@/storage/products'
import { ShoppingBasket } from 'lucide-vue-next'
import { convertPriceToEuro } from '@/utils/price/convertPrice'
import { formatPrice } from '@/event/locale'
import { getProductName } from '@/utils/product/getProductName'
import { hasDescriptionForLanguage } from '@/utils/product/hasDescriptionForLanguage'

const props = defineProps<{
    product: Product
    index: number
    truncatedDescriptions: string[]
    propsFunc: (el: HTMLDivElement, index: number) => void
}>()
</script>

<template>
    <section class="catalog-select-product-happy-with-card">
        <img
            loading="lazy"
            :src="`${URL_IMAGE}${props.product.image}`"
            :alt="getProductName(props.product)"
            class="img-happy"
            :class="{
                'img-clean-happy': props.product.hasCleanImg,
                'img-with-bg-happy': !props.product.hasCleanImg
            }"
        />


        <div class="div-card-price-happy">
            <svg
                class="svg-card-price-happy"
                viewBox="0 0 50 37"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
            >
                <g filter="url(#filter0_d_169_5884)">
                    <path
                        d="M35.3654 30.1875H17.0775C12.335 30.1875 9.96372 30.1875 8.17523 29.214C6.82195 28.4773 5.71018 27.3655 4.97354 26.0123C4 24.2238 4 20.694 4 13.6343C4 9.92305 4 8.06742 5.32046 6.06409C6.31431 4.55628 7.78908 3.42908 9.50488 2.86584C11.7846 2.1175 14.6929 2.9086 20.5095 4.49079L38.1588 9.29165C40.008 9.79465 40.9326 10.0461 41.6979 10.4451C43.8015 11.5416 45.31 13.5152 45.8159 15.833C46 16.6761 46 17.635 46 19.5529C46 22.0029 46 23.2279 45.7339 24.2337C44.9982 27.0142 42.8267 29.1857 40.0462 29.9214C39.0404 30.1875 37.8154 30.1875 35.3654 30.1875Z"
                    />
                </g>
                <defs>
                    <filter
                        id="filter0_d_169_5884"
                        x="0"
                        y="0.581055"
                        filterUnits="userSpaceOnUse"
                        color-interpolation-filters="sRGB"
                    >
                        <feFlood flood-opacity="0" result="BackgroundImageFix" />

                        <feOffset dy="2" />
                        <feGaussianBlur stdDeviation="2" />
                        <feComposite in2="hardAlpha" operator="out" />
                        <feColorMatrix
                            type="matrix"
                            values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.25 0"
                        />
                        <feBlend
                            mode="normal"
                            in2="BackgroundImageFix"
                            result="effect1_dropShadow_169_5884"
                        />
                        <feBlend
                            mode="normal"
                            in="SourceGraphic"
                            in2="effect1_dropShadow_169_5884"
                            result="shape"
                        />
                    </filter>
                </defs>
            </svg>

            <p class="p-card-price-happy">
                {{ formatPrice(props.product.price) }}
            </p>
        </div>

        <div
            class="div-card-description-happy"
            :ref="(el) => propsFunc(el as HTMLDivElement, props.index)"
        >
            <div class="div-card-name-happy">
                <h2 class="h2-card-name-happy">{{ getProductName(props.product) }}</h2>
                <p
                    class="p-card-name-happy"
                    v-if="
                        hasDescriptionForLanguage(
                            props.product.description,
                            languageIdBus
                        )
                    "
                >
                    {{ truncatedDescriptions[index] }}
                </p>
            </div>

            <button class="div-select-product-happy">
                <ShoppingBasket class="svg-size-select-product-happy" :size="20" :stroke-width="1.5" />
            </button>
        </div>
    </section>
</template>

<style scoped>
.catalog-select-product-happy-with-card {
    @apply w-full flex relative rounded-xl overflow-visible;
    /* refactored */
    @apply bg-t-card border border-t-border text-t-text;
    aspect-ratio: 281 / 215;
    transition: background-color 0.2s ease, border-color 0.2s ease;
}

.img-happy {
    @apply absolute inset-0 -top-6  w-5/6 h-3/5 object-contain rounded-xl z-10;
}

.img-clean-happy {
    @apply object-contain w-5/6 -left-2;
}

.img-with-bg-happy {
    @apply object-cover w-4/6 left-3;
}
/* .img-happy-no-description {
    @apply absolute inset-0 top-2 left-5 w-3/6 h-2/3 object-contain rounded-xl z-10;
} */
/* svg-card-product removed — card background is now CSS */

.div-card-price-happy {
    @apply absolute z-10 top-[28%] right-[3%] flex items-center justify-center;
    /* refactored */
    @apply text-t-text;
}

.svg-card-price-happy {
    /* refactored */
    @apply w-[4.5rem] fill-t-card;
}

.p-card-price-happy {
    @apply absolute text-sm inset-0 flex items-center justify-center text-center m-auto font-semibold;
    /* refactored */
    @apply text-t-text;
    @apply happy-ifo-dark:tabular-nums happy-ifo-dark:tracking-wide;
}

.div-card-description-happy {
    @apply absolute top-[45%] bottom-3 transform text-center min-w-full text-sm;
    @apply cursor-pointer font-semibold flex flex-col items-start pl-4;
    /* refactored */
    @apply text-t-text;
    /* z-index: 10; */
}

.div-card-name-happy {
    @apply overflow-hidden w-[97%];
    /* @apply happy-gray-sand:text-bg-happy-light; */
}

.h2-card-name-happy {
    @apply text-wrap text-start w-3/5;
    text-overflow: ellipsis;
}

.p-card-name-happy {
    @apply font-normal text-start text-xs w-4/6 overflow-hidden;
    transition: all 0.3s ease;
}

.div-select-product-happy {
    @apply absolute right-[7%] top-6 w-8 h-7 border-2 flex items-center justify-center rounded-lg;
    /* refactored */
    @apply bg-t-accent border-t-accent;
    @apply happy-ifo-dark:bg-t-bg happy-ifo-dark:border-t-border;
}

.svg-size-select-product-happy {
    /* refactored - lucide icon uses stroke */
    @apply stroke-t-on-accent;
    @apply happy-ifo-dark:stroke-t-on-accent;
}

/* Active */

.catalog-select-product-happy-with-card:active,
.catalog-select-product-happy-with-card:focus {
    /* refactored */
    @apply bg-t-accent border-t-accent;
}

.catalog-select-product-happy-with-card:active .p-card-price-happy,
.catalog-select-product-happy-with-card:focus .p-card-price-happy {
    /* refactored */
    @apply text-t-on-accent;
    @apply happy-ifo-dark:text-t-accent;
}

.catalog-select-product-happy-with-card:active .div-card-description-happy,
.catalog-select-product-happy-with-card:focus .div-card-description-happy {
    /* refactored */
    @apply text-t-on-accent;
    @apply happy-ifo-dark:text-t-accent;
}

.catalog-select-product-happy-with-card:active .svg-card-price-happy,
.catalog-select-product-happy-with-card:focus .svg-card-price-happy {
    /* refactored */
    @apply fill-t-card;
}

.catalog-select-product-happy-with-card:active .div-select-product-happy,
.catalog-select-product-happy-with-card:focus .div-select-product-happy {
    /* refactored */
    @apply bg-t-accent-hover border-t-border;
    @apply happy-ifo-dark:bg-t-card happy-ifo-dark:border-t-border;
}

.catalog-select-product-happy-with-card:active .svg-size-select-product-happy,
.catalog-select-product-happy-with-card:focus .svg-size-select-product-happy {
    /* refactored */
    @apply stroke-t-on-accent;
    @apply happy-ifo-dark:stroke-t-on-accent;
}

/* Hover */

@media (hover: hover) {
    .catalog-select-product-happy-with-card:hover {
        /* refactored */
        @apply bg-t-accent border-t-accent;
    }

    .catalog-select-product-happy-with-card:hover .p-card-price-happy {
        /* refactored */
        @apply text-t-on-accent;
    @apply happy-ifo-dark:text-t-accent;
    }

    .catalog-select-product-happy-with-card:hover .div-card-description-happy {
        /* refactored */
        @apply text-t-on-accent;
    @apply happy-ifo-dark:text-t-accent;
    }

    .catalog-select-product-happy-with-card:hover .svg-card-price-happy {
        /* refactored */
        @apply fill-t-card;
    }

    .catalog-select-product-happy-with-card:hover .div-select-product-happy {
        /* refactored */
        @apply bg-t-accent-hover border-t-border;
        @apply happy-ifo-dark:bg-t-card happy-ifo-dark:border-t-border;
    }

    .catalog-select-product-happy-with-card:hover .svg-size-select-product-happy {
        /* refactored */
        @apply stroke-t-on-accent;
        @apply happy-ifo-dark:stroke-t-on-accent;
    }
}

@media screen and (min-width: 370px) {
    .div-select-product-happy {
        @apply w-9 h-8 top-8;
    }
    .svg-size-select-product-happy {
        @apply w-6 h-6;
    }
    .div-card-price-happy {
        @apply top-[30%]  right-[4%]  flex items-center justify-center;
    }
    /* .img-happy-no-description {
        @apply -top-2;
    } */
}

@media screen and (min-width: 500px) {
    .div-select-product-happy {
        @apply w-9 h-8 top-6;
    }
}

@media screen and (min-width: 900px) {
    .div-card-description-happy {
        @apply pl-4;
    }
}
</style>
